import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Autocomplete,
  TextField,
  Chip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";
import TableComponent from "components/grid";
import { useDispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import PaginationComponent from "components/ui-components/PaginationComponent";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAllRiskQuery } from "store/slices/apis/riskPackageSlice";
import moment from "moment";
import { useDeleteKeyRiskMutation } from "store/slices/apis/keyRiskSlice";
import { IoMdClose, IoMdSearch } from "react-icons/io";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { useGetAllChangeQuery } from "store/slices/apis/changeTrackerPackageSlice";
import EditChangeTracker from "./EditChangeTracker";
import AddChangeTracker from "./AddChangeTracker";
import { useDeleteKeyChangeMutation } from "store/slices/apis/keyChangeTrackerSlice";
import { usePermission } from "hooks/usePermission";
import { CHANGE_ORDER_VIEW, CHANGE_ORDER_EDIT, CHANGE_ORDER_DELETE, CHANGE_ORDER_CREATE } from "config";
import { ReactComponent as AddNewIcon } from "assets/PwC_Funct_Icons_Plus_Outline_Black_RGB.svg"
import { ReactComponent as SearchIcon } from "assets/PwC_Funct_Icons_Search_Outline_Black_RGB.svg"
import { ReactComponent as CrossIcon } from "assets/PwC_Funct_Icons_Close_Outline_Black_RGB.svg"
import { ReactComponent as CalendarIcon } from "assets/PwC_Funct_Icons_Calendar_Outline_Black_RGB.svg"

const columns = [
  {
    name: "Change Tracker ID",
    field: "change_number",
    renderer(params) {
      return (
        <Stack direction="row" spacing={1}>
          {params?.change_number && (
            <Chip
              label={params?.change_number}
              size="small"
              sx={{
                fontSize: "12px",
                "& .MuiChip-label": {
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  fontWeight: 600,
                },
              }}
            />
          )}
        </Stack>
      );
    },
  },
  {
    name: "Category",
    field: "package",
  },
  {
    name: "Sub Category 1",
    field: "sub_package1",
  },
  {
    name: "Workflow Status",
    field: "approved_status",
    renderer(params) {
      return (
        <>
          {params?.approved_status === "Pending_for_Approval"
            ? "Pending for approval"
            : params?.approved_status}
        </>
      );
    },
  },
  {
    name: "Current Status",
    field: "status",
  },
  {
    name: "Created Date",
    field: "createddate",
    renderer(params) {
      return (
        <>
          {params?.createddate
            ? moment(params.createddate).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Contractor Number",
    field: "contractor_number",
  },
  {
    name: "Consultant/Contractor",
    field: "consultant_contractor",
  },
  {
    name: "Change Raised To",
    field: "change_raised_to",
  },
  {
    name: "Change Received On",
    field: "change_received_on",
    renderer(params) {
      return (
        <>
          {params?.change_received_on
            ? moment(params.change_received_on).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Change Raised By Reference",
    field: "change_raised_by_refrence",
  },
  {
    name: "Change Raised By Date Received",
    field: "change_raised_by_date_received",
    renderer(params) {
      return (
        <>
          {params?.change_raised_by_date_received
            ? moment(params.change_raised_by_date_received).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  // {
  //   name: "Reason For Change",
  //   field: "reason_for_change",
  // },
  {
    name: "Change Raised To Date Of Response",
    field: "change_raised_to_date_of_response",
    renderer(params) {
      return (
        <>
          {params?.change_raised_to_date_of_response
            ? moment(params.change_raised_to_date_of_response).format(
              "DD/MM/YY"
            )
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Change Raised To Position",
    field: "change_raised_to_position",
  },
  {
    name: "Approved Cost Impact",
    field: "approved_cost_impact",
  },
  {
    name: "Change Raised By Estimate Cost Impact",
    field: "change_raised_by_estimate_cost_impact",
  },
  {
    name: "Change Raised To Estimate Cost Impact",
    field: "change_raised_to_estimate_cost_impact",
  },
  {
    name: "Change Raised By Estimate Time Impact",
    field: "change_raised_by_estimate_time_impact",
  },
  {
    name: "Change Raised To Estimate Time Impact",
    field: "change_raised_to_estimate_time_impact",
  },
  {
    name: "Approved Time Impact",
    field: "approved_time_impact",
  },
  {
    name: "Actual Date Of Closure",
    field: "actual_date_of_closure",
    renderer(params) {
      return (
        <>
          {params?.actual_date_of_closure
            ? moment(params.actual_date_of_closure).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  // {
  //   name: "Description Of Change",
  //   field: "description_of_change",
  // },
  {
    name: "Action",
    field: "action",
    option: {},
  },
];

const approvedStatuses = [
  { label: "Pending for approval", value: "Pending_for_Approval" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
];

const AllChangeTracker = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const changeOrderViewAccess = usePermission(CHANGE_ORDER_VIEW);
  const changeOrderEditAccess = usePermission(CHANGE_ORDER_EDIT);
  const changeOrderDeleteAccess = usePermission(CHANGE_ORDER_DELETE);
  const changeOrderAddAccess = usePermission(CHANGE_ORDER_CREATE);

  const [showAddIssue, setShowAddIssue] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [isEditIssueModal, setEditIssueModal] = useState(false);
  const [issueData, setEditIssueData] = useState(null);
  const [isViewIssueModal, setIsViewIssueModal] = useState(false);
  const [approvedStatus, setApprovedStatus] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [filters, setFilters] = useState({
    approvedStatus: "",
    fromDate: "",
    toDate: "",
  });
  const { projectId } = useParams();
  const {
    data: allChange,
    refetch,
    isLoading,
  } = useGetAllChangeQuery({
    ProjectId: projectId,
    sortBy: "createddate",
    sortOrder: "desc",
    approved_status: "Approved",
    from: filters?.fromDate,
    to: filters?.toDate,
    page: pageNumber,
    pageSize: 10,
  });
  const [changeDeleteFn, changeDeleteRes] = useDeleteKeyChangeMutation();

  const handleAddIssue = () => {
    setShowAddIssue(!showAddIssue);
  };

  const openNotification = async (msg, type = "error") => {
    await dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: "alert",
        alert: {
          color: type,
        },
        close: false,
      })
    );

    setTimeout(() => dispatch(closeSnackbar()), 3000);
  };

  const CustomCalendarIcon = (props) => (
    <CalendarIcon {...props} style={{ width: 26, height: 26, fill: "#696969 ", stroke: "#696969", strokeWidth: 4 }} />
  );

  const handleActionEdit = (row) => {
    setEditIssueModal(!isEditIssueModal);
    if (!issueData) {
      setEditIssueData(row);
    } else {
      setEditIssueData(null);
    }
  };

  const encrypt = (text) => btoa(unescape(encodeURIComponent(text)));

  const handleActionView = (row) => {
    const encrypted = encrypt("view");
    navigate(`/projects/${projectId}/change-detail/${row?.change_id}/${encrypted}/false`);

    // setIsViewIssueModal(!isViewIssueModal);
    // if (!issueData) {
    //   setEditIssueData(row);
    // } else {
    //   setEditIssueData(null);
    // }
  };
  const handleActionDelete = (row) => {
    setDeleteItem(row);
    setDeleteModal(true);
  };

  const handleConfirmationDelete = () => {
    changeDeleteFn(deleteItem?.change_id);
  };

  useEffect(() => {
    if (changeDeleteRes?.isSuccess) {
      if (changeDeleteRes?.data?.status == "success") {
        openNotification(changeDeleteRes?.data?.message, "success");
        refetch();
      } else {
        openNotification(changeDeleteRes?.data?.message, "error");
      }
      handleCloseDelete();
    }
  }, [changeDeleteRes?.isSuccess]);

  const handleCloseDelete = () => {
    setDeleteItem(null);
    setDeleteModal(false);
  };

  const confirmationDeleteDialog = () => (
    <Dialog open={isDeleteModal} aria-labelledby="draggable-dialog-title">
      <DialogTitle
        sx={{
          fontWeight: 500,
          fontSize: "20px",
          color: "#252525",
          paddingBottom: 0,
        }}
        id="draggable-dialog-title"
      >
        Delete
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleCloseDelete}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        {/* <IoMdClose style={{ fontSize: "20px" }} /> */}
        <CrossIcon style={{ fontSize: "20px", width: "20px", height: "20px", fill: "#808080" }} />
      </IconButton>
      <DialogContent>
        <DialogContentText
          sx={{ fontWeight: 400, fontSize: "16px", color: "#474747" }}
        >
          Are you sure you want to Delete this change tracker? This action is
          permanent.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleCloseDelete}
          sx={{
            color: "#415385",
            fontSize: "16px",
            fontWeight: "500",
            textTransform: "none",
            border: "1px solid #415385",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmationDelete}
          loading={changeDeleteRes?.isLoading}
          variant="contained"
          sx={{
            backgroundColor: "#C52A1A",
            color: "#fff",
            fontSize: "16px",
            fontWeight: "500",
            textTransform: "none",
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {confirmationDeleteDialog()}
      <Grid2 container spacing={2} sx={{ mt: "20px" }}>
        <Grid2 size={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: 'start', sm: 'center' },
              justifyContent: "space-between",
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                // flexGrow: 2,
                alignItems: { xs: "stretch", sm: "center" }, // Full width on mobile
                width: { xs: '100%', sm: 'auto' }, // Stack takes full width
                "& .MuiStack-root": {
                  paddingTop: "15px",
                },
              }}
            >
              {/* <Autocomplete
                           options={approvedStatuses}
                           getOptionLabel={(option) => option?.label}
                           onChange={(_, newValue) =>
                             setApprovedStatus(newValue ? newValue?.value : "")
                           }
                           renderInput={(params) => (
                             <TextField
                               {...params}
                               label="Current Status"
                               name="approvedStatus"
                               // slotProps={{ inputLabel: { shrink: true } }}
                               variant="outlined"
                               size="small"
                               sx={{
                                 "& .MuiFilledInput-root": {
                                   backgroundColor: "#fff",
                                 },
           
                                 "& .MuiInputLabel-root": {
                                   top: "-3px",
                                 },                    
                                 minWidth: "230px",
                               }}
                             />
                           )}
                           ListboxProps={{
                             sx: { fontSize: "13px" },
                           }}
                           // sx={{ width: "23%", marginTop: "7px !important" }}
                         /> */}
              <LocalizationProvider dateAdapter={AdapterDayjs} size="small">
                <DemoContainer components={["DatePicker", "DatePicker"]}>
                  <DatePicker
                    label="From Date"
                    format="DD-MM-YYYY"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    slots={{
                      openPickerIcon: CustomCalendarIcon,
                    }}
                    slotProps={{
                      textField: {
                        sx: {
                          minWidth: 150,
                          "& .MuiOutlinedInput-input": {
                            padding: "10px",
                          },

                          "& .MuiInputLabel-root": {
                            top: "-6px",
                          },
                        },
                      },
                      field: {
                        clearable: true,
                        onClear: () =>
                          setFilters({
                            fromDate: "",
                            toDate: toDate
                              ? dayjs(toDate).format("YYYY-MM-DD")
                              : "",
                            approvedStatus,
                          }),
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker", "DatePicker"]}>
                  <DatePicker
                    label="To Date"
                    format="DD-MM-YYYY"
                    value={toDate}
                    clearable={true}
                    onChange={(newValue) => setToDate(newValue)}
                    slots={{
                      openPickerIcon: CustomCalendarIcon,
                    }}
                    slotProps={{
                      textField: {
                        sx: {
                          minWidth: 150,
                          "& .MuiOutlinedInput-input": {
                            padding: "10px",
                          },

                          "& .MuiInputLabel-root": {
                            top: "-6px",
                          },
                        },
                      },
                      field: {
                        clearable: true,
                        onClear: () =>
                          setFilters({
                            fromDate: fromDate
                              ? dayjs(fromDate).format("YYYY-MM-DD")
                              : "",
                            toDate: "",
                            approvedStatus,
                          }),
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
              <Button
                loading={isLoading}
                onClick={() => {
                  setFilters({
                    fromDate: fromDate
                      ? dayjs(fromDate).format("YYYY-MM-DD")
                      : "",
                    toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
                    approvedStatus,
                  });
                }}
                size="large"
                // startIcon={<IoMdSearch />}
                startIcon={<SearchIcon style={{ height: "20px", width: "20px", fill: `${theme?.customSettings?.themeColor}` }} />}
                variant="outlined"
                sx={{
                  color: `${theme?.customSettings?.themeColor}`,
                  border: `1px solid ${theme?.customSettings?.themeColor}`,
                  background: "none",
                  textTransform: "inherit",
                  padding: "6px 20px",
                  marginTop: "12px !important",
                }}
              >
                Search
              </Button>
              <Button
                loading={isLoading}
                onClick={() => {
                  setFromDate(null);
                  setToDate(null);
                  const clearedValues = Object.fromEntries(
                    Object.keys(filters).map((key) => [key, ""])
                  );
                  setFilters(clearedValues);
                }}
                size="large"
                variant="outlined"
                sx={{
                  color: `${theme?.customSettings?.themeColor}`,
                  border: `1px solid ${theme?.customSettings?.themeColor}`,
                  background: "none",
                  textTransform: "inherit",
                  padding: "6px 15px",
                  marginTop: "12px !important",
                }}
              >
                Reset
              </Button>
            </Stack>
            {changeOrderAddAccess &&
              <Button
                variant="contained"
                onClick={handleAddIssue}
                // startIcon={<AddIcon sx={{ marginBottom: "2px" }} />}
                startIcon={<AddNewIcon style={{ height: "20px", width: "20px", fill: "#FFFFFF" }} />}
                sx={{
                  backgroundColor: `${theme?.customSettings?.themeColor}`,
                  color: "#FFF",
                  padding: "5px 20px",
                  fontWeight: "500",
                  textTransform: "none",
                  fontSize: "16px",
                  marginTop: "11px",
                  border: `1px solid ${theme?.customSettings?.themeColor}`,
                }}
              >
                Add Change Order
              </Button>
            }

          </Box>
        </Grid2>

        <Grid2 size={12} sx={{ mt: 2 }}>
          <TableComponent
            columns={columns
              .map((col) =>
                col.field === "action"
                  ? {
                    ...col,
                    option: {
                      ...col.option,
                      view: changeOrderViewAccess,
                      delete: changeOrderDeleteAccess,
                      edit: changeOrderEditAccess,
                    },
                  }
                  : col
              )
              .filter((col) => {
                if (col.field !== "action") {
                  return true;
                } else {
                  return Object.values(col.option).some((v) => v === true);
                }

              })}
            data={allChange?.data?.filter((risk) => risk?.active === true)}
            actionDelete={handleActionDelete}
            actionEdit={handleActionEdit}
            actionView={handleActionView}
            isLoading={isLoading}
          />
          {!isLoading && (
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <PaginationComponent
                pageNumber={allChange?.pagination?.currentPage || 1}
                totalPages={allChange?.pagination?.totalPages || 1}
                onPageChange={setPageNumber}
              />
            </Box>
          )}
        </Grid2>
      </Grid2>
      {showAddIssue && (
        <AddChangeTracker
          cancelIssue={handleAddIssue}
          open={showAddIssue}
          openNotification={openNotification}
          refetch={refetch}
        />
      )}
      {isEditIssueModal && (
        <EditChangeTracker
          cancelIssue={handleActionEdit}
          open={isEditIssueModal}
          data={issueData}
          openNotification={openNotification}
          refetch={refetch}
        />
      )}

      {isViewIssueModal && (
        <EditChangeTracker
          cancelIssue={handleActionView}
          open={isViewIssueModal}
          data={issueData}
          openNotification={openNotification}
          isViewIssueModal
        />
      )}
    </>
  );
};

export default AllChangeTracker;
